import { formatDate } from '../../Modules/Shared/Utils/date.utils';
import { IntuneGroup } from './intune-group.model';

export class IntuneDevice {
  id?: string;
  userId?: string;
  deviceName?: string;
  displayName?: string; // displayName is the same as deviceName
  ownerType?: string;
  managedDeviceOwnerType?: string;
  managementState?: string;
  chassisType?: string;
  operatingSystem?: string;
  deviceType?: string;
  jailBroken?: string;
  managementAgent?: string;
  easActivated?: boolean | null;
  easDeviceId?: string;
  easActivationDateTime?: string | null;
  aadRegistered?: boolean | null;
  azureADRegistered?: boolean | null;
  deviceEnrollmentType?: string;
  lostModeState?: string;
  activationLockBypassCode?: string | null;
  emailAddress?: string;
  azureActiveDirectoryDeviceId?: string;
  deviceRegistrationState?: string;
  deviceCategoryDisplayName?: string;
  isSupervised?: boolean | null;
  exchangeLastSuccessfulSyncDateTime?: string | null;
  exchangeAccessState?: string;
  exchangeAccessStateReason?: string;
  remoteAssistanceSessionUrl?: string;
  remoteAssistanceSessionErrorDetails?: string;
  isEncrypted?: boolean | null;
  userPrincipalName?: string;
  enrolledByUserPrincipalName?: string;
  model?: string;
  manufacturer?: string;
  imei?: string | null;
  complianceGracePeriodExpirationDateTime?: string | null;
  serialNumber?: string;
  phoneNumber?: string | null;
  androidSecurityPatchLevel?: string | null;
  userDisplayName?: string;
  configurationManagerClientEnabledFeatures?: any | null;
  wiFiMacAddress?: string | null;
  deviceHealthAttestationState?: any | null;
  subscriberCarrier?: string;
  meid?: string | null;
  totalStorageSpaceInBytes?: number;
  freeStorageSpaceInBytes?: number;
  managedDeviceName?: string;
  partnerReportedThreatState?: string;
  retireAfterDateTime?: string | null;
  preferMdmOverGroupPolicyAppliedDateTime?: string | null;
  autopilotEnrolled?: boolean | null;
  requireUserEnrollmentApproval?: boolean | null;
  managementCertificateExpirationDate?: string | null;
  iccid?: string;
  udid?: string;
  roleScopeTagIds?: string[];
  windowsActiveMalwareCount?: number;
  windowsRemediatedMalwareCount?: number;
  notes?: string | null;
  configurationManagerClientHealthState?: any | null;
  configurationManagerClientInformation?: any | null;
  ethernetMacAddress?: string;
  physicalMemoryInBytes?: number;
  processorArchitecture?: string;
  specificationVersion?: string | null;
  joinType?: string;
  skuFamily?: string;
  securityPatchLevel?: string | null;
  skuNumber?: number;
  managementFeatures?: string;
  enrollmentProfileName?: string;
  bootstrapTokenEscrowed?: boolean | null;
  deviceFirmwareConfigurationInterfaceManaged?: boolean | null;
  hardwareInformation?: HardwareInformation;
  deviceActionResults?: DeviceActionResult[];
  detectedApps?: DetectedApp[];
  usersLoggedOn?: any[];
  chromeOSDeviceInfo?: any[];
  supplementalDeviceDetails?: any[];
  deviceIdentityAttestationDetail?: DeviceIdentityAttestationDetail;
  groups?: IntuneGroup[];

  // operatingSystemVersion is the same as osVersion
  osVersion?: string;
  operatingSystemVersion?: string;

  // deviceId is the same as azureADDeviceId
  azureADDeviceId?: string;
  deviceId?: string;

  // isCompliant is the same as complianceState
  complianceState?: string;
  isCompliant?: boolean;

  // approximateLastSignInDateTime is the same as lastSyncDateTime
  lastSyncDateTime?: string | null;
  approximateLastSignInDateTime?: string;

  // registrationDateTime is the same as enrolledDateTime
  enrolledDateTime?: string | null;
  registrationDateTime?: string;

  // agent last seen is not a property from Graph API
  agent_last_log?: string;

  constructor(data?: Partial<IntuneDevice>) {
    Object.assign(this, data);
  }

  displayDeviceName(fallback: string = 'shared.general.labels.na'): string {
    return this.deviceName || this.displayName || fallback;
  }

  displayUser(fallback: string = 'shared.general.labels.na'): string {
    return this.userDisplayName || fallback;
  }

  displayEmail(fallback: string = 'shared.general.labels.na'): string {
    return this.userPrincipalName || this.emailAddress || fallback;
  }

  displayOperatingSystem(fallback: string = 'shared.general.labels.na'): string {
    const osName = this.operatingSystem || '';
    const osVersion = this.osVersion || '';
    const osDisplay = [osName, osVersion].filter(Boolean).join(' ');
    return osDisplay || fallback;
  }

  displayCompliance(): boolean | null {
    if (this.complianceState != undefined) {
      switch (this.complianceState) {
        case 'Compliant':
        case 'compliant':
          return true;
        case 'Non-compliant':
        case 'noncompliant':
          return false;
        default:
          return null;
      }
    }

    if (this.isCompliant != undefined) {
      switch (this.isCompliant) {
        case true:
          return true;
        case false:
          return false;
        default:
          return null;
      }
    }

    return null;
  }

  displayLastSync(fallback: string = 'shared.general.labels.na'): string {
    if (this.lastSyncDateTime != undefined) {
      return new Date(this.lastSyncDateTime).toLocaleString();
    }

    if (this.approximateLastSignInDateTime != undefined) {
      return new Date(this.approximateLastSignInDateTime).toLocaleString();
    }

    return fallback;
  }

  displayEnrolledDateTime(fallback: string = 'shared.general.labels.na'): string {
    if (this.enrolledDateTime != undefined) {
      return new Date(this.enrolledDateTime).toLocaleString();
    }

    if (this.registrationDateTime != undefined) {
      return new Date(this.registrationDateTime).toLocaleString();
    }

    return fallback;
  }

  routeId(): string {
    return this.deviceId || this.azureADDeviceId || this.id || '';
  }

  get storageUsagePercentage(): number {
    const total = this.totalStorageSpaceInBytes ?? 0;
    const free = this.freeStorageSpaceInBytes ?? 0;
    if (total === 0) return 0;
    const used = total - free;
    return Math.round((used / total) * 100);
  }

  get agent(): string|null {
    return this.agent_last_log ? formatDate(this.agent_last_log) : null;
  }
}

export class HardwareInformation {
  serialNumber?: string | null;
  totalStorageSpace?: number;
  freeStorageSpace?: number;
  imei?: string | null;
  meid?: string | null;
  manufacturer?: string | null;
  model?: string | null;
  phoneNumber?: string | null;
  subscriberCarrier?: string | null;
  cellularTechnology?: string | null;
  wifiMac?: string | null;
  operatingSystemLanguage?: string | null;
  isSupervised?: boolean | null;
  isEncrypted?: boolean | null;
  batterySerialNumber?: string | null;
  batteryHealthPercentage?: number;
  batteryChargeCycles?: number;
  isSharedDevice?: boolean | null;
  tpmSpecificationVersion?: string | null;
  operatingSystemEdition?: string | null;
  deviceFullQualifiedDomainName?: string | null;
  deviceGuardVirtualizationBasedSecurityHardwareRequirementState?: string;
  deviceGuardVirtualizationBasedSecurityState?: string;
  deviceGuardLocalSystemAuthorityCredentialGuardState?: string;
  osBuildNumber?: string | null;
  operatingSystemProductType?: number;
  ipAddressV4?: string | null;
  subnetAddress?: string | null;
  esimIdentifier?: string | null;
  systemManagementBIOSVersion?: string | null;
  tpmManufacturer?: string | null;
  tpmVersion?: string | null;
  wiredIPv4Addresses?: string[];
  batteryLevelPercentage?: number | null;
  residentUsersCount?: number | null;
  productName?: string | null;
  deviceLicensingStatus?: string;
  deviceLicensingLastErrorCode?: number;
  deviceLicensingLastErrorDescription?: string | null;
  sharedDeviceCachedUsers?: any[];

  constructor(data?: Partial<HardwareInformation>) {
    Object.assign(this, data);
  }
}

export class DeviceActionResult {
  actionName?: string;
  actionState?: string;
  startDateTime?: string | null;
  lastUpdatedDateTime?: string | null;

  constructor(data?: Partial<DeviceActionResult>) {
    Object.assign(this, data);
  }
}

export class DetectedApp {
  id?: string;
  displayName?: string;
  version?: string;
  sizeInByte?: number;
  deviceCount?: number;
  publisher?: string | null;
  platform?: string;

  constructor(data?: Partial<DetectedApp>) {
    Object.assign(this, data);
  }
}

export class DeviceIdentityAttestationDetail {
  deviceIdentityAttestationStatus?: string;

  constructor(data?: Partial<DeviceIdentityAttestationDetail>) {
    Object.assign(this, data);
  }
}
